/**
 * Retrieves the authorization header based on the specified SSO (Single Sign-On) token type.
 *
 * @returns {string|boolean} - Returns the authorization header in the format `Bearer <token>`
 *                             if a valid token is found. Returns `false` if no valid token is available.
 */
export const getAuthHeader = () => {
    // Fallback to the mapping logic for certain clients if no auth header is found
    const configMap = [
        {
            condition: () => window.location.host.includes('tennistv'),
            getter: getAtpMediaTokenFromLocalStorage
        },
        {
            condition: () => window.location.host.includes('afl'),
            getter: getAflOktaTokenFromLocalStorage
        },
        {
            condition: () => window.location.host.includes('wta'),
            getter: getWtaTokenFromLocalStorage
        },
        {
            condition: () => window.location.host.includes('spl'),
            getter: getSplTokenFromLocalStorage
        },
        {
            condition: () => window.location.host.includes('cricket.com.au'),
            getter: getCaTokenFromLocalStorage
        },
        {
            condition: () =>
                window.location.host.includes('tottenhamhotspur') ||
                window.location.host.includes('thfc'),
            getter: getTHFCAuthToken
        }
    ];

    for (const config of configMap) {
        if (config.condition()) {
            const jwt = config.getter();
            if (jwt) {
                return `Bearer ${jwt}`;
            }
        }
    }

    // Final fallback to localStorage if all else fails - incase a Pulselive ID client has forgotten
    // to configure this properly.
    const fallbackJwt = localStorage.getItem('kc_token') || null;
    return fallbackJwt ? `Bearer ${fallbackJwt}` : false;
};

/**
 * Checks if the user is signed in based on the specified SSO (Single Sign-On) token type.
 *
 * @returns {boolean} - Returns `true` if a valid authorization header is found, indicating the user is signed in.
 *                      Returns `false` if no valid authorization header is available.
 */
export const checkForSignIn = () => {
    const authHeader = getAuthHeader();

    if (authHeader) {
        return true;
    }

    return false;
};

/**
 * Retrieves the `kc_auth_token` in local storage.
 *
 * @returns {string|null} The `kc_auth_token` if found, or `null` if not found or an error occurs.
 */
export function getSplTokenFromLocalStorage() {
    // Retrieve the spl data from local storage
    const splToken = localStorage.getItem('kc_auth_token');

    // Check if the data exists
    if (!splToken) {
        console.warn('No data found under the key "kc_auth_token".');
        return null;
    }

    return splToken;
}

/**
 * Retrieves the `kc_auth_token` in local storage.
 *
 * @returns {string|null} The `kc_auth_token` if found, or `null` if not found or an error occurs.
 */
export function getAtpMediaTokenFromLocalStorage() {
    // Retrieve the atp data from local storage
    const atpToken = localStorage.getItem('kc_auth_token');

    // Check if the data exists
    if (!atpToken) {
        console.warn('No data found under the key "kc_auth_token".');
        return null;
    }

    return atpToken;
}

/**
 * Retrieves the `idToken` from the `okta-token-storage` key in local storage.
 *
 * This function checks if the `okta-token-storage` key exists in local storage, parses the
 * stored JSON string into an object, and retrieves the `idToken` property if available.
 *
 * @returns {string|null} The `idToken` if found, or `null` if not found or an error occurs.
 */
export function getAflOktaTokenFromLocalStorage() {
    const oktaValue = localStorage.getItem('okta-token-storage') || null;

    // Check if the data exists
    if (!oktaValue) {
        console.warn('No data found under the key "okta-token-storage".');
        return null;
    }

    try {
        // Parse the JSON string to an object
        const parsedOktaValue = JSON.parse(oktaValue);

        // Check if idToken exists in the parsed object
        if (parsedOktaValue?.idToken?.idToken) {
            return parsedOktaValue.idToken.idToken;
        }
        console.warn('idToken not found in the afl object.');
        return null;
    } catch (error) {
        console.error('Error parsing afl data:', error);
        return null;
    }
}

/**
 * Retrieves the `kc_token` in local storage.
 *
 * @returns {string|null} The `kc_token` if found, or `null` if not found or an error occurs.
 */
export function getWtaTokenFromLocalStorage() {
    // Retrieve the wta data from local storage
    const wtaToken = localStorage.getItem('kc_token');

    // Check if the data exists
    if (!wtaToken) {
        return null;
    }

    return wtaToken;
}

/**
 * Retrieves the `cid_token` in local storage.
 *
 * @returns {string|null} The `cid_token` if found, or `null` if not found or an error occurs.
 */
export function getCaTokenFromLocalStorage() {
    // Retrieve the Cricket Australia data from local storage
    const caToken = localStorage.getItem('cid_token');

    // Check if the data exists
    if (!caToken) {
        console.warn('No data found under the key "cid_token".');
        return null;
    }

    return caToken;
}

/**
 * Retrieves the THFC auth token from either the global `window` object or local storage.
 * This will allow webviews on apps to pass in the token as a global variable.
 *
 * @returns {string|null} The THFC auth token if found, or `null` if not found.
 */
export function getTHFCAuthToken() {
    // Retrieve the THFC auth token from window or local storage
    const thfcToken =
        window.access_token || localStorage.getItem('access_token');

    // Check if the data exists
    if (!thfcToken) {
        console.warn('No token found under the key "access_token".');
        return null;
    }

    return thfcToken;
}

/**
 * Extracts the userId (sub claim) from a JWT auth header
 *
 * @param {string} authHeader - The Authorization header (with or without 'Bearer ' prefix)
 * @returns {string|null} - The userId from the JWT's sub claim, or null if extraction fails
 */
export const getUserIdFromAuthHeader = (authHeader) => {
    const BEARER_PREFIX = 'Bearer ';
    const JWT_PARTS_COUNT = 3;
    const JWT_PAYLOAD_INDEX = 1;

    if (!authHeader || typeof authHeader !== 'string') {
        return null;
    }

    try {
        // Remove 'Bearer ' prefix if present
        const token = authHeader.startsWith(BEARER_PREFIX)
            ? authHeader.slice(BEARER_PREFIX.length)
            : authHeader;

        // JWT has 3 parts: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== JWT_PARTS_COUNT) {
            return null;
        }

        // Decode the payload (second part)
        const payload = JSON.parse(atob(parts[JWT_PAYLOAD_INDEX]));
        return payload.sub || null;
    } catch (error) {
        console.warn('Failed to extract userId from auth header:', error);
        return null;
    }
};
